import { CookiePreferencesButton } from "../CookiePreferences";
import { type ReactNode } from "react";
import { Link } from "@/components/ui/shadcn/link";
import { cn, animDelay } from "@/lib/utils";
import { Text } from "@/components/ui/Typography";
import { AuthBrandScene } from "./AuthBrandScene";

interface AuthLayoutProps {
  children: ReactNode;
  className?: string;
  illustration?: ReactNode;
}

export function AuthLayout({
  children,
  className,
  illustration,
}: AuthLayoutProps) {
  return (
    <div className="auth-shell relative flex min-h-[100dvh] flex-col overflow-hidden bg-ink">
      {/* Subtle geometric pattern overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: "32px 32px",
        }}
      />

      {/* Soft ambient glow */}
      <div className="pointer-events-none absolute -top-1/4 left-1/2 h-[600px] w-[800px] -translate-x-1/2 rounded-full bg-primary/[0.1] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-1/4 right-0 h-[400px] w-[600px] rounded-full bg-white/[0.03] blur-3xl" />

      <div className="relative flex flex-1 items-center justify-center px-4 py-8 sm:py-10">
        <div
          className={cn(
            "flex w-full max-w-[1300px] flex-col items-center gap-10 lg:flex-row lg:items-center lg:gap-14 xl:gap-20",
            className,
          )}
        >
          <div className="anim-pop-in mx-auto w-full max-w-xl lg:mx-0 lg:max-w-[440px]">
            <div
              className="auth-form-panel relative rounded-xl bg-background p-7 sm:p-10 shadow-2xl
              ring-1 ring-white/20
              before:pointer-events-none before:absolute before:inset-x-0 before:top-0
              before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent
              before:rounded-t-xl"
            >
              {children}
            </div>
          </div>

          <div
            className="anim-slide-in-right hidden flex-1 items-center lg:flex"
            style={animDelay(200)}
          >
            <div className="ml-auto flex w-full max-w-[680px] justify-center">
              {illustration ?? <AuthBrandScene />}
            </div>
          </div>
        </div>
      </div>

      <footer
        className="anim-fade-in relative px-6 pb-6 pt-2 text-center text-white/80"
        style={animDelay(600)}
      >
        <Text variant="caption" className="text-white/60">
          By using the platform, you agree to the{" "}
          <Link
            to="https://rentinit.ae/terms/"
            className="font-medium text-white/80 transition-colors hover:text-white"
          >
            Terms of Use
          </Link>
          ,{" "}
          <Link
            to="https://rentinit.ae/privacy/"
            className="font-medium text-white/80 transition-colors hover:text-white"
          >
            Privacy Policy
          </Link>
          , and{" "}
          <Link
            to="https://rentinit.ae/cookie-policy/"
            className="font-medium text-white/80 transition-colors hover:text-white"
          >
            Cookies Policy
          </Link>
          .
        </Text>
        <CookiePreferencesButton className="mt-3 text-xs text-white/80 hover:text-white" />
      </footer>
    </div>
  );
}
